// src/components/CustomerLandingPage.js

import React, { useMemo, useState } from 'react';
import { Box, Grid, Text, Paper, Button, Stack, Group } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { gql, useQuery } from '@apollo/client';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  if (amount === 0) return '$0';
  const absAmount = Math.abs(amount).toLocaleString();
  return amount < 0 ? `-$${absAmount}` : `$${absAmount}`;
};

// Define GraphQL Query
const GET_CUSTOMER_LANDING_PAGE = gql`
  query GetCustomerLandingPage($acctNo: String!) {
    customerLandingDetailsByAccountNumber(acctNo: $acctNo) {
      acctNo
      name
      lastQuoteDate
      lastSalesOrderDate
      lastCallTrackerDate
      healthStatus
      creditRemaining
      creditLimit
      ytdRevenue
      ytdGoal
      netNewOrders
      openOrders
    }
  }
`;

const InfoItem = ({ label, value }) => (
  <Group justify="space-between" gap="xs">
    <Text size="sm" c="dimmed" fw={500}>
      {label}:
    </Text>
    <Text size="sm" fw={600}>
      {value}
    </Text>
  </Group>
);

const Section = ({ title, children, buttonText, onViewMore }) => (
  <Paper p="xs" withBorder>
    <Stack gap="xs">
      <Text fw={700} size="md" c="dark">
        {title}
      </Text>
      <Stack gap={4}>
        {children}
      </Stack>
      <Button 
        variant="filled" 
        size="xs"
        rightSection={<IconArrowRight size={14} />}
        onClick={onViewMore}
        fullWidth
        mt={4}
      >
        {buttonText}
      </Button>
    </Stack>
  </Paper>
);

const CustomerLandingPage = ({ setActiveTab, selectedCustomer }) => {
  const { data, loading, error } = useQuery(GET_CUSTOMER_LANDING_PAGE, {
    variables: { acctNo: selectedCustomer?.acctNo || '' },
    skip: !selectedCustomer,
    fetchPolicy: 'network-only',
  });

  if (loading) return <Text p="xs">Loading...</Text>;
  if (error) return <Text p="xs" c="red">Error: {error.message}</Text>;
  if (!data) return <Text p="xs">No data available</Text>;

  const customerData = data.customerLandingDetailsByAccountNumber;

  return (
    <Box p="xs">
      <Grid gutter="xs">
        {/* Production Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Production" onViewMore={() => setActiveTab(2)} buttonText="Production">
            <InfoItem label="Last Quote Date" value={formatDate(customerData.lastQuoteDate)} />
            <InfoItem label="Last Sales Date" value={formatDate(customerData.lastSalesOrderDate)} />
            <InfoItem label="Last Call Tracker" value={formatDate(customerData.lastCallTrackerDate)} />
          </Section>
        </Grid.Col>

        {/* Status Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Status" onViewMore={() => setActiveTab(1)} buttonText="Profile">
            <InfoItem label="Health" value={customerData.healthStatus || 'N/A'} />
            <InfoItem label="Credit Remaining" value={formatCurrency(customerData.creditRemaining)} />
            <InfoItem label="Credit Limit" value={formatCurrency(customerData.creditLimit)} />
          </Section>
        </Grid.Col>

        {/* Performance Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Performance" onViewMore={() => setActiveTab(3)} buttonText="Performance">
            <InfoItem label="Year to Date Revenue" value={formatCurrency(customerData.ytdRevenue)} />
            <InfoItem label="Annual Goal" value={formatCurrency(customerData.ytdGoal)} />
            <InfoItem label="Net New Orders" value={formatCurrency(customerData.netNewOrders)} />
            <InfoItem label="Open Orders" value={formatCurrency(customerData.openOrders)} />
          </Section>
        </Grid.Col>

        {/* Proficiency Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Proficiency" onViewMore={() => setActiveTab(4)} buttonText="Proficiency">
            <InfoItem label="Account Number" value={customerData.acctNo} />
            <InfoItem label="Customer Name" value={customerData.name} />
          </Section>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default CustomerLandingPage;